import {useState} from 'react'
// import dayjs from 'dayjs'
import {v4 as uuidv4} from 'uuid'
import cn from 'clsx'
import {Space} from 'antd'

import styles from './RequestForecastList.module.scss'
import {DrawdownsSurplusModal} from 'modules/project'
import {useUpdateForecastItem, useUpdateForecast} from 'common/api'
import {useOpen} from 'common/hooks'
import {
  Circle,
  Text,
  CentsMoney,
  IconButton,
  PenIcon,
  CloseSecondIcon,
  RotateArrowFourthIcon,
} from 'common/components'
import {
  formatDate,
  isSameYearMonth,
  arrayFromMonthsCount,
  changeForecastDrawdownItem,
  isItemDrawdown,
  isItemRepayment,
  getDrawdownsSurplus,
} from 'common/helpers'
import {SetNewForecast, ForecastFormItem} from 'common/types'
import {RequestForecastModal} from '.'

interface RequestForecastListProps {
  fixedForecast?: ForecastFormItem[]
  forecast: ForecastFormItem[]
  projectUuid?: string
  requestUuid?: string
  openDate: Date
  months: number
  loanValue: number
  setNewForecast?: SetNewForecast
  hideEmpty?: boolean
  // variationDate?: Date
}

interface ForecastItemProps {
  projectUuid?: string
  requestUuid?: string
  minDate: Date
  prevDate?: Date
  item: ForecastFormItem
  number: number
  loanValue?: number
  forecast?: ForecastFormItem[]
  setNewForecast?: SetNewForecast
}

interface RemoveButtonProps {
  forecastItem: ForecastFormItem
  projectUuid?: string
  requestUuid?: string
  forecast?: ForecastFormItem[]
  loanValue?: number
  disabled?: boolean
  setNewForecast?: SetNewForecast
}

interface EmptyMonthProps {
  date: Date
  projectUuid: string
  requestUuid: string
  minDate: Date
  loanValue: number
  forecast: ForecastFormItem[]
  setNewForecast?: SetNewForecast
}

export default function RequestForecastList({
  fixedForecast,
  forecast,
  projectUuid,
  requestUuid,
  months,
  openDate,
  loanValue,
  setNewForecast,
  hideEmpty,
}: RequestForecastListProps) {
  let drawdownCount = 0
  let repaymentCount = 1
  if (!!fixedForecast?.length) {
    fixedForecast.forEach((x:ForecastFormItem) => {
      x.readOnly = true
    })
    // fixedForecast[0].initialDrawdown = true
  }
  if (!!forecast?.length) {
    forecast.forEach((x:ForecastFormItem) => {
      x.readOnly = false
    })
    // forecast[0].initialDrawdown = true
  }
  const monthsArray = arrayFromMonthsCount(months + 1, openDate)
  const fullMonthsForecast: ForecastFormItem[] = []
  monthsArray.forEach((date) => {
    if (
      fixedForecast?.some((item) => isSameYearMonth(item.date, date)) ||
      forecast.some((item) => isSameYearMonth(item.date, date)) 
    ) {
      fullMonthsForecast.push(
        ...(fixedForecast?.filter((item) => isSameYearMonth(item.date, date)) || []),
        ...forecast.filter((item) => isSameYearMonth(item.date, date))
      )
    } else {
      fullMonthsForecast.push({
        date,
        uuid: uuidv4(),
        type: null,
        amount: null,
      })
    }
  })
  // console.table(forecast)
  // console.table(fullMonthsForecast)

  return (
    <div className={styles.root}>
      {fullMonthsForecast.map((item, index) =>
        item.type ? (
          <ForecastItem
            // key={item.uuid}
            key={index}
            projectUuid={projectUuid}
            requestUuid={requestUuid}
            minDate={openDate}
            number={
              ( (!!item.initialDrawdown) && (drawdownCount !== 0) ) ? drawdownCount :
              isItemDrawdown(item) ? drawdownCount++ : repaymentCount++
            }
            prevDate={
              index === 0 ? undefined : fullMonthsForecast[index - 1].date
            }
            item={item}
            loanValue={loanValue}
            forecast={forecast}
            setNewForecast={setNewForecast}
          />
        ) : !hideEmpty ? (
          <EmptyMonth
            key={item.uuid}
            date={item.date}
            projectUuid={projectUuid}
            requestUuid={requestUuid}
            minDate={openDate}
            loanValue={loanValue}
            forecast={forecast}
            setNewForecast={setNewForecast}
          />
        ) : null
      )}
    </div>
  )
}

const ForecastItem = ({
  projectUuid,
  requestUuid,
  minDate,
  prevDate,
  item,
  number,
  loanValue,
  forecast,
  setNewForecast,
}: ForecastItemProps) => {
  const [isOpen, handleOpen, handleClose] = useOpen()
  const isVariationInitialDD = (number !== 0) && (item.readOnly === false) && (item.initialDrawdown === true)
  const isRepayment = isItemRepayment(item)
  const samePrevMonth = isSameYearMonth(prevDate, item.date)
  const amountJSX = <CentsMoney as="span" value={item.amount} />

  return (
    <>
      <div className={
        (item.readOnly !== true) ? styles.item :
        cn(styles.item, styles.locked)
      }>
        <div className={cn(styles.grid, !samePrevMonth && styles.grid_divider)}>
          { (item.readOnly === true) ?  
            <div className={styles.dot}>
              <Circle border color="grey" />
            </div>
            :
            (item.readOnly === false) ? 
            <div className={styles.dot}>
              <Circle border color="turquoize" />
            </div>
            :
            null
          }
          <Text weight="demiBold">
            {samePrevMonth ? null : formatDate(item.date, 'fourth')}
          </Text>
          <Space>
            {isRepayment && <RotateArrowFourthIcon />}
            <span>
              { 
                (isVariationInitialDD) ? 'Est. variation initial drawdown' :
                ((!isRepayment) && (number === 0)) ? 'Initial drawdown' :
                `${(item.transactions || item.readOnly) ? '' : 'Est. '}${item.type} ${number}`}
            </span>
          </Space>
          <Text size="xs" color="grey" align="center">
            {formatDate(item.date, 'fifth')}
          </Text>
          <Text
            weight="demiBold"
            align="right"
            color={
              item.transactions
                ? 'grey'
                : item.variation
                ? 'turquoizeBright'
                : undefined
            }
          >
            {isRepayment ? (
              <>
                <Text as="span" color="grey">
                  (
                </Text>
                {amountJSX}
                <Text as="span" color="grey">
                  )
                </Text>
              </>
            ) : (
              amountJSX
            )}
          </Text>
        </div>
        { (item.readOnly !== true) &&
        <Space className={styles.actions} size={0}>
          <IconButton
            className={styles.action}
            icon={<PenIcon />}
            onClick={handleOpen}
          />
          <RemoveButton
            forecastItem={item}
            projectUuid={projectUuid}
            requestUuid={requestUuid}
            forecast={forecast}
            loanValue={loanValue}
            disabled={!prevDate}
            setNewForecast={setNewForecast}
          />
        </Space>
        }
      </div>
      <RequestForecastModal
        open={isOpen}
        projectUuid={projectUuid}
        requestUuid={requestUuid}
        minDate={minDate}
        loanValue={loanValue}
        forecastItem={item}
        forecast={forecast}
        setNewForecast={setNewForecast}
        onClose={handleClose}
      />
    </>
  )
}

const RemoveButton = ({
  forecastItem,
  projectUuid,
  requestUuid,
  forecast,
  loanValue,
  disabled,
  setNewForecast,
}: RemoveButtonProps) => {
  const [drawdownsSurplus, setDrawdownsSurplus] = useState(0)
  const [isOpen, handleOpen, handleClose] = useOpen()
  const {mutate: deleteForecastItem, isMutating: isDeleteMutating} =
    useUpdateForecastItem(projectUuid, forecastItem.uuid)
  const {mutate: updateForecast, isMutating: isUpdateForecastMutating} =
    useUpdateForecast(projectUuid, forecastItem.uuid)

  const hasForecast = Boolean(projectUuid)
  const isRemoveDisabled =
    (hasForecast && forecastItem.amount === 0) || disabled
  const isForecastDrawdown = hasForecast && isItemDrawdown(forecastItem)
  const isDrawdownAllocate = isForecastDrawdown && drawdownsSurplus > 0

  const handleRemove = () => {
    if (!hasForecast) {
      return setNewForecast((state) => {
        let newForecast = state
        if (isItemDrawdown(forecastItem)) {
          newForecast = state.map((newForecastItem) =>
            newForecastItem.uuid === forecastItem.uuid
              ? {...newForecastItem, amount: 0}
              : newForecastItem
          )
          newForecast = changeForecastDrawdownItem({
            loanValue,
            forecast: newForecast,
            drawdown: {...forecastItem, amount: 0},
          })
        }
        return newForecast.filter(
          (newForecastItem) => newForecastItem.uuid !== forecastItem.uuid
        )
      })
    }
    const drawdowns = forecast.filter((item) => isItemDrawdown(item))
    const surplus = getDrawdownsSurplus(
      drawdowns,
      forecastItem.date,
      loanValue,
      undefined,
      forecastItem.uuid
    )
    if (isForecastDrawdown && surplus > 0) {
      setDrawdownsSurplus(surplus)
      handleOpen()
      return
    }
    handleDeleteSingleItem()
  }

  const handleDeleteSingleItem = () =>
    deleteForecastItem({
      date: formatDate(forecastItem.date, 'server'),
      amount: 0,
    })

  return (
    <>
      <IconButton
        className={styles.action}
        icon={<CloseSecondIcon />}
        loading={isDeleteMutating || isUpdateForecastMutating}
        disabled={isRemoveDisabled}
        onClick={handleRemove}
      />
      {isDrawdownAllocate && (
        <DrawdownsSurplusModal
          surplus={drawdownsSurplus}
          isOpen={isOpen}
          values={{
            type: forecastItem.type,
            date: forecastItem.date,
            amount: 0,
          }}
          forecastItem={forecastItem}
          loanValue={loanValue}
          projectUuid={projectUuid}
          requestUuid={requestUuid}
          forecast={forecast}
          onClose={handleClose}
          onSingleItemChange={handleDeleteSingleItem}
          updateForecast={updateForecast}
        />
      )}
    </>
  )
}

const EmptyMonth = ({
  date,
  projectUuid,
  requestUuid,
  minDate,
  loanValue,
  forecast,
  setNewForecast,
}: EmptyMonthProps) => {
  const [isOpen, handleOpen, handleClose] = useOpen()

  return (
    <>
      <button
        className={cn(styles.item, styles.item_button)}
        onClick={handleOpen}
        disabled={date < minDate}
      >
        <div className={cn(styles.grid, styles.grid_divider)}>
          <Text weight="demiBold">{formatDate(date, 'fourth')}</Text>
        </div>
      </button>
      <RequestForecastModal
        open={isOpen}
        projectUuid={projectUuid}
        requestUuid={requestUuid}
        minDate={minDate}
        itemDate={date}
        loanValue={loanValue}
        forecast={forecast}
        setNewForecast={setNewForecast}
        onClose={handleClose}
      />
    </>
  )
}
